import React, { forwardRef } from "react";
import { InputSwitch } from "primereact/inputswitch";
import { MultiSelect } from "primereact/multiselect";
import { classNames } from "primereact/utils";
import { useTranslation } from "react-i18next";
import { DashlyFilterClearField } from "../inputs/DashlyFilterClearField/DashlyFilterClearField";
import {
    DashlyFilterDropdown,
    type DashlyFilterDropdownProps,
} from "../inputs/DashlyFilterDropdown/DashlyFilterDropdown";
import {
    DashlyFilterMultiSelect,
    type DashlyFilterMultiSelectProps,
} from "../inputs/DashlyFilterMultiSelect/DashlyFilterMultiSelect";
import { DashlyFilterSearch } from "../inputs/DashlyFilterSearch/DashlyFilterSearch";
import { DashlyControlSize } from "../inputs/shared/dashlyControlSize";
import "../inputs/DashlyFilterClearField/DashlyFilterClearField.scss";
import "./PageFilters.scss";

export interface PageFiltersProps {
    children: React.ReactNode;
    className?: string;
    /** Wrap fields in a form (SpoofyCrud-style). */
    asForm?: boolean;
    onSubmit?: React.FormEventHandler<HTMLFormElement>;
    /** Optional actions rendered to the right of the filter fields (e.g. refresh). */
    actions?: React.ReactNode;
}

export const PageFilters: React.FC<PageFiltersProps> = ({
    children,
    className,
    asForm = false,
    onSubmit,
    actions,
}) => {
    const fields = <div className="spoofy-filters__fields">{children}</div>;
    const actionsNode = actions ? <div className="spoofy-filters__actions">{actions}</div> : null;

    if (asForm) {
        return (
            <div className={classNames("spoofy-filters", className)}>
                <form className="spoofy-filters__form" onSubmit={onSubmit}>
                    {fields}
                    {actionsNode}
                </form>
            </div>
        );
    }

    if (actionsNode) {
        return (
            <div className={classNames("spoofy-filters", className)}>
                <div className="spoofy-filters__form">
                    {fields}
                    {actionsNode}
                </div>
            </div>
        );
    }

    return <div className={classNames("spoofy-filters", className)}>{fields}</div>;
};

export interface PageFilterSearchProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    inputId?: string;
    leadingIcon?: string;
    controlSize?: DashlyControlSize;
}

export const PageFilterSearch: React.FC<PageFilterSearchProps> = ({ className, ...props }) => (
    <div className={classNames("spoofy-filter-field spoofy-filter-field--search", className)}>
        <DashlyFilterSearch {...props} />
    </div>
);

export interface PageFilterToggleProps {
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    /** Ties the label to the switch; give each toggle on a page its own. */
    inputId: string;
    disabled?: boolean;
    className?: string;
}

/** A yes/no filter: the same label-plus-switch box a SpoofyCrud page renders for one, so a hand-built
 *  filter bar has it in the house style without repeating the markup. */
export const PageFilterToggle: React.FC<PageFilterToggleProps> = ({
    label,
    checked,
    onChange,
    inputId,
    disabled = false,
    className,
}) => (
    <div className={classNames("spoofy-filter-field spoofy-filter-field--toggle", className)}>
        <label className="spoofy-filter-field__label spoofy-filter-field__label--visible" htmlFor={inputId}>
            {label}
        </label>
        <InputSwitch
            inputId={inputId}
            className="spoofy-filter-field__toggle-control"
            checked={checked}
            disabled={disabled}
            onChange={(event) => onChange(Boolean(event.value))}
        />
    </div>
);

export interface PageFilterDropdownProps extends DashlyFilterDropdownProps {
    clearValue?: unknown;
    onClear?: () => void;
}

export const PageFilterDropdown: React.FC<PageFilterDropdownProps> = ({
    className,
    controlSize = "md",
    filter = true,
    ...rest
}) => (
    <div className={classNames("spoofy-filter-field", className)}>
        <DashlyFilterDropdown
            controlSize={controlSize}
            className="w-full spoofy-filter-field__control"
            filter={filter}
            {...rest}
        />
    </div>
);

export interface PageFilterMultiSelectProps extends DashlyFilterMultiSelectProps {
    clearValue?: unknown[];
    onClear?: () => void;
}

export const PageFilterMultiSelect = forwardRef<MultiSelect, PageFilterMultiSelectProps>(
    function PageFilterMultiSelect(
        { value, clearValue = [], onClear, onChange, className, controlSize = "md", showClear: _showClear, ...rest },
        ref,
    ) {
        const { t } = useTranslation();
        const selected = Array.isArray(value) ? value : [];
        const hasValue = selected.length > 0;
        const clearLabel = t("common.action.clear_filter");

        const handleClear = () => {
            onChange?.({ value: clearValue } as Parameters<NonNullable<typeof onChange>>[0]);
            onClear?.();
        };

        return (
            <div className="spoofy-filter-field">
                <DashlyFilterClearField
                    hasValue={hasValue}
                    onClear={handleClear}
                    clearLabel={clearLabel}
                    controlType="select"
                    controlSize={controlSize}
                    className="spoofy-filter-field__control"
                >
                    <DashlyFilterMultiSelect
                        ref={ref}
                        value={value}
                        onChange={onChange}
                        controlSize={controlSize}
                        className={classNames("w-full", className)}
                        {...rest}
                    />
                </DashlyFilterClearField>
            </div>
        );
    },
);

PageFilterMultiSelect.displayName = "PageFilterMultiSelect";
